<script setup lang="ts">
import { NSpace, NIcon, NFlex, NAvatar, NText, NBadge } from "naive-ui"
import { useAuth } from "@/composables/auth/useAuth"
import { HeartOutline, SearchSharp } from "@vicons/ionicons5"
import { watch, computed, ref } from "vue"
import { useProfilePictureRefresh } from "@/composables/profile/useProfilePictureRefresh"
import { useNotifications } from "@/composables/notification/useNotifications"
import { useRoute, useRouter } from "vue-router"
import NotificationsDrawer from "@/components/notification/NotificationsDrawer.vue"

const { user } = useAuth()
const { refreshTrigger, cachedProfilePictureUrl, loadProfilePicture } = useProfilePictureRefresh()
const { unreadCount } = useNotifications()
const route = useRoute()
const router = useRouter()

const emit = defineEmits<{
  menu: []
}>()

const showNotifications = ref(false)

// Watch for user changes (login/logout)
watch(
  user,
  () => {
    loadProfilePicture(user.value?.id)
  },
  { immediate: true },
)

// Watch for manual refresh triggers (e.g., after profile picture upload)
watch(refreshTrigger, () => {
  loadProfilePicture(user.value?.id)
})

// Map route names to page titles
const pageTitle = computed(() => {
  const routeName = route.name?.toString()
  switch (routeName) {
    case "Profile":
      return "Profile"
    case "ProfileEdit":
      return "Edit Profile"
    case "Settings":
      return "Settings"
    case "Account":
      return "Account"
    case "Security":
      return "Security"
    case "Subscription":
      return "Subscription"
    case "Appearance":
      return "Appearance"
    case "AppInfo":
      return "App Info"
    case "Activity":
      return "Activity Log"
    case "PrivacySettings":
      return "Privacy Settings"
    case "PrivacyPolicy":
      return "Privacy Policy"
    case "SocialPrivacy":
      return "Social & Privacy"
    default:
      return null
  }
})

// Determine if search icon should be shown
const showSearch = computed(() => {
  const routeName = route.name?.toString()
  return routeName === "Books" || routeName === "Settings"
})

// Determine search behavior based on route
const isSettingsRoute = computed(() => route.name?.toString() === "Settings")

function handleSearchClick() {
  if (isSettingsRoute.value) {
    // Navigate to settings with search query parameter
    router.push({ name: "Settings", query: { search: "true" } })
  } else {
    // Navigate to search page for books
    router.push({ name: "Search Libary" })
  }
}
</script>

<template>
  <header class="app-header" role="banner">
    <nav aria-label="Main navigation">
      <n-space justify="space-between" align="center">
        <n-flex align="center" :size="12">
          <n-avatar
            v-if="user"
            @click="emit('menu')"
            bordered
            round
            size="large"
            :src="cachedProfilePictureUrl || undefined"
            alt="User profile picture" />
          <n-text v-if="pageTitle" strong class="page-title">
            {{ pageTitle }}
          </n-text>
        </n-flex>
        <n-flex align="start" justify="start" :size="12">
          <button
            v-if="user"
            class="p-0"
            @click="showNotifications = true"
            aria-label="Notifications">
            <n-badge dot :show="unreadCount > 0">
              <n-icon size="30"><HeartOutline /></n-icon>
            </n-badge>
          </button>
          <button v-if="showSearch" class="p-0" @click="handleSearchClick">
            <n-icon size="30"><SearchSharp /></n-icon>
          </button>
        </n-flex>
      </n-space>
    </nav>

    <!-- Notifications Drawer -->
    <NotificationsDrawer v-model:show="showNotifications" />
  </header>
</template>

<style scoped>
.page-title {
  font-size: 20px;
}
</style>
